/**
 * WORKFLOW VALIDATOR -- State Machine cho chuyển đổi trạng thái
 *
 * Single Source of Truth cho transition rules giữa backend và frontend.
 * Backend dùng để validate, frontend dùng để hiển thị allowed transitions.
 */

import type {
  QuotationStatus,
  OrderStatus,
  ProductionOrderStatus,
  PurchaseOrderStatus,
  DeliveryStatus,
} from './enums';
import { getStatusLabel, type StatusRegistryKey } from './registry';

export type EntityType = 'QUOTATION' | 'ORDER' | 'PRODUCTION_ORDER' | 'PURCHASE_ORDER' | 'DELIVERY';

const ENTITY_STATUS_REGISTRY: Record<EntityType, StatusRegistryKey> = {
  QUOTATION: 'QuotationStatus',
  ORDER: 'OrderStatus',
  PRODUCTION_ORDER: 'ProductionOrderStatus',
  PURCHASE_ORDER: 'PurchaseOrderStatus',
  DELIVERY: 'DeliveryStatus',
};

const ALLOWED_APPROVER_ROLES = ['MANAGER', 'ADMIN', 'DIRECTOR'];

/**
 * Định nghĩa các trạng thái tiếp theo hợp lệ cho mỗi trạng thái hiện tại.
 */
export const STATE_MACHINE_CONFIG: Record<EntityType, Record<string, string[]>> = {
  QUOTATION: {
    'DRAFT': ['SENT', 'PROCESSING', 'ESTIMATED', 'REJECTED'],
    'SENT': ['PROCESSING', 'ESTIMATED', 'APPROVED', 'REJECTED'],
    // "Lưu" đưa báo giá sang Đang xử lý (chờ tính giá); giá bán do sale nhập nên vẫn gửi khách được (FB-NCF9YD)
    'PROCESSING': ['SENT', 'ESTIMATED', 'APPROVED', 'REJECTED'],
    'ESTIMATED': ['APPROVED', 'PROCESSING', 'SENT', 'REJECTED'],
    'APPROVED': ['REJECTED'],
    'REJECTED': ['DRAFT', 'PROCESSING'],
  },
  ORDER: {
    'DRAFT': ['PENDING', 'CONFIRMED', 'APPROVED', 'CANCELLED'],
    'PENDING': ['WAITING_PRE_PRESS', 'CONFIRMED', 'APPROVED', 'CANCELLED'],
    'WAITING_PRE_PRESS': ['CONFIRMED', 'APPROVED', 'CANCELLED'],
    'CONFIRMED': ['APPROVED', 'PLANNED', 'IN_PRODUCTION', 'IN_PROGRESS', 'COMPLETED', 'DELIVERED', 'CANCELLED'],
    'APPROVED': ['CONFIRMED', 'PLANNED', 'IN_PRODUCTION', 'IN_PROGRESS', 'COMPLETED', 'DELIVERED', 'CANCELLED'],
    'PLANNED': ['IN_PRODUCTION', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'],
    'IN_PROGRESS': ['COMPLETED', 'DELIVERED', 'CANCELLED'],
    'IN_PRODUCTION': ['COMPLETED', 'DELIVERED', 'CANCELLED'],
    'COMPLETED': ['DELIVERED', 'CLOSED'],
    'DELIVERED': ['INVOICED', 'CLOSED', 'COMPLETED'],
    'INVOICED': ['CLOSED'],
    'CLOSED': [],
    'CANCELLED': ['DRAFT', 'PENDING'],
  },
  PRODUCTION_ORDER: {
    'DRAFT': ['PENDING_APPROVAL', 'PRE_PRESS', 'READY_FOR_SCHEDULING', 'PLANNED', 'RELEASED', 'CANCELLED'],
    // Chờ duyệt ban hành (bấm "Lưu"): như nháp nhưng mọi người thấy; người có quyền duyệt ban hành
    'PENDING_APPROVAL': ['DRAFT', 'PRE_PRESS', 'READY_FOR_SCHEDULING', 'PLANNED', 'RELEASED', 'CANCELLED'],
    'PRE_PRESS': ['PRE_PRESS_COMPLETED', 'READY_FOR_SCHEDULING', 'PLANNED', 'RELEASED', 'CANCELLED', 'PAUSED', 'ON_HOLD'],
    'PRE_PRESS_COMPLETED': ['READY_FOR_SCHEDULING', 'MATERIALS_READY', 'PLANNED', 'RELEASED', 'IN_PROGRESS', 'CANCELLED', 'PAUSED', 'ON_HOLD'],
    'READY_FOR_SCHEDULING': ['MATERIALS_READY', 'PLANNED', 'RELEASED', 'IN_PROGRESS', 'CANCELLED', 'PAUSED', 'ON_HOLD'],
    'MATERIALS_READY': ['PLANNED', 'RELEASED', 'IN_PROGRESS', 'CANCELLED', 'PAUSED', 'ON_HOLD'],
    'PLANNED': ['RELEASED', 'IN_PROGRESS', 'CANCELLED', 'PAUSED', 'ON_HOLD'],
    'RELEASED': ['IN_PROGRESS', 'CANCELLED', 'PAUSED', 'ON_HOLD'],
    'IN_PROGRESS': ['MANUFACTURED', 'COMPLETED', 'PAUSED', 'ON_HOLD', 'CANCELLED'],
    'MANUFACTURED': ['COMPLETED', 'IN_PROGRESS', 'CANCELLED', 'PAUSED', 'ON_HOLD'], // mở lại khi sản lượng ghi sai (rà soát 31/10/2026)
    'PAUSED': ['IN_PROGRESS', 'PLANNED', 'RELEASED', 'CANCELLED'],
    'ON_HOLD': ['IN_PROGRESS', 'PLANNED', 'RELEASED', 'CANCELLED'],
    'COMPLETED': ['CLOSED', 'CANCELLED'],
    'CLOSED': [],
    'CANCELLED': ['DRAFT'],
  },
  PURCHASE_ORDER: {
    'DRAFT': ['PENDING', 'CANCELLED'],
    'PENDING': ['APPROVED', 'CANCELLED'],
    'APPROVED': ['ORDERED', 'CANCELLED'],
    'ORDERED': ['PARTIAL_RECEIVED', 'RECEIVED', 'CANCELLED'],
    'PARTIAL_RECEIVED': ['RECEIVED', 'CANCELLED'],
    'RECEIVED': [],
    'CANCELLED': ['DRAFT'],
  },
  DELIVERY: {
    'DRAFT': ['SCHEDULED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED'],
    'SCHEDULED': ['IN_TRANSIT', 'DELIVERED', 'DRAFT', 'CANCELLED'],
    'IN_TRANSIT': ['DELIVERED', 'SCHEDULED', 'CANCELLED'],
    'DELIVERED': ['COMPLETED'],
    'COMPLETED': [],
    'CANCELLED': ['DRAFT'],
  }
};

/**
 * Trạng thái yêu cầu quyền đặc biệt (MANAGER/ADMIN/DIRECTOR).
 */
export const RESTRICTED_TARGET_STATES: Record<string, string[]> = {
  'APPROVED': ALLOWED_APPROVER_ROLES,
  'CONFIRMED': ALLOWED_APPROVER_ROLES,
  'REJECTED': ALLOWED_APPROVER_ROLES,
  'RELEASED': ALLOWED_APPROVER_ROLES,
};

// ── Error Messages tiếng Việt ──

const ENTITY_LABELS: Record<EntityType, string> = {
  QUOTATION: 'Báo giá',
  ORDER: 'Đơn hàng',
  PRODUCTION_ORDER: 'Lệnh sản xuất',
  PURCHASE_ORDER: 'Đơn mua hàng',
  DELIVERY: 'Phiếu giao hàng',
};

/**
 * Lấy danh sách trạng thái tiếp theo hợp lệ.
 * Frontend dùng để hiển thị dropdown cho phép chuyển trạng thái.
 */
export function getAllowedTransitions(entityType: EntityType, currentState: string): string[] {
  const config = STATE_MACHINE_CONFIG[entityType];
  if (!config) return [];
  return config[currentState] || [];
}

export class WorkflowValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkflowValidationError';
  }
}

export class WorkflowValidator {
  /**
   * Kiểm tra chuyển trạng thái có hợp lệ không.
   * Throw error với message tiếng Việt nếu không hợp lệ.
   */
  static validateTransition(entityType: EntityType, currentState: string, nextState: string): void {
    if (currentState === nextState) return;

    const config = STATE_MACHINE_CONFIG[entityType];
    if (!config) {
      throw new WorkflowValidationError(
        `Không tìm thấy cấu hình quy trình cho loại: ${ENTITY_LABELS[entityType] || entityType}`
      );
    }

    const allowedNextStates = config[currentState] || [];
    if (!allowedNextStates.includes(nextState)) {
      const entityLabel = ENTITY_LABELS[entityType] || entityType;
      // Báo bằng nhãn tiếng Việt, không lộ mã trạng thái
      const label = (s: string) => {
        const key = ENTITY_STATUS_REGISTRY[entityType];
        return key ? getStatusLabel(key, s) : s;
      };
      throw new WorkflowValidationError(
        `Không thể chuyển ${entityLabel} từ "${label(currentState)}" sang "${label(nextState)}". ` +
        `Có thể chuyển sang: ${allowedNextStates.length > 0 ? allowedNextStates.map(label).join(', ') : '(không có)'}`
      );
    }
  }

  /**
   * Kiểm tra quyền chuyển sang trạng thái mục tiêu.
   */
  static validateRoleAccess(targetState: string, userRole: string): void {
    const requiredRoles = RESTRICTED_TARGET_STATES[targetState];
    if (requiredRoles && !requiredRoles.includes(userRole?.toUpperCase())) {
      throw new WorkflowValidationError(
        `Bạn không có quyền chuyển sang trạng thái "${targetState}". Yêu cầu vai trò: ${requiredRoles.join(', ')}`
      );
    }
  }

  /**
   * Kiểm tra toàn bộ: transition + role access.
   */
  static validate(entityType: EntityType, currentState: string, nextState: string, userRole: string): void {
    this.validateTransition(entityType, currentState, nextState);
    if (userRole) {
      this.validateRoleAccess(nextState, userRole);
    }
  }
}

/** Lệnh SX đã chạy sản xuất (có sản lượng): đơn hàng không được huỷ kéo theo, phải dừng / hoàn tất lệnh trước */
export const STARTED_PO_STATUSES = ['IN_PROGRESS', 'MANUFACTURED'];

/** Lệnh tạm dừng / tạm giữ / đã xong: công đoạn không được bắt đầu từ xưởng (mở lại qua quy trình của lệnh) */
export const PO_NOT_STARTABLE_STATUSES = ['PAUSED', 'ON_HOLD', 'MANUFACTURED', 'COMPLETED'];
/** Lệnh chưa ban hành / đã huỷ / đóng: không ghi sản lượng, không bắt đầu công đoạn */
export const PO_NOT_RECORDABLE_STATUSES = ['DRAFT', 'PENDING_APPROVAL', 'CANCELLED', 'CLOSED'];
/** Lệnh ở các bước chuẩn bị: công đoạn đầu tiên bắt đầu thì lệnh chuyển Đang sản xuất */
export const PO_START_PROMOTES_STATUSES = ['PRE_PRESS_COMPLETED', 'READY_FOR_SCHEDULING', 'MATERIALS_READY', 'PLANNED', 'RELEASED'];
